import { REWARDS_ART } from '@/assets/rewards';
import { AVATAR_ART } from '@/assets/rewards/avatars';
import type { AvatarFrameId, ScaleAvatar } from '@/lib/api/rewards-hub/models';

/**
 * How a player's portrait is put together: the art, and the frames worn over it.
 *
 * Figma 13449:469105 (the picker), 13449:469833 (the frame tab) and
 * 13449:470197 (the fifty-six portraits and their four states).
 */

/** Shown while `/scale` has not answered, and for a position nothing is drawn for. */
export const AVATAR_FALLBACK = REWARDS_ART['avatars-preview.webp'];

/** Ranks run 1..6; the top one drops sixteen portraits, the rest eight apiece. */
const AVATARS_PER_RANK: Record<number, number> = { 1: 8, 2: 8, 3: 8, 4: 8, 5: 8, 6: 16 };

/**
 * The portrait for a catalogue entry.
 *
 * The backend's own `im` wins whenever it is there — it is the real catalogue,
 * and an admin can change it. The local files are only a stand-in for the
 * stretch where `im` is still null (the FE notes: "`im` часто null — арт ещё не
 * залит"), keyed by the same rank-and-position the catalogue is ordered by.
 */
export function avatarArt(avatar: Pick<ScaleAvatar, 'image' | 'rankIndex' | 'position'>): string {
  if (avatar.image) return avatar.image;
  const count = AVATARS_PER_RANK[avatar.rankIndex];
  if (!count || avatar.position < 1 || avatar.position > count) return AVATAR_FALLBACK;
  return AVATAR_ART[`rank-${avatar.rankIndex}-${avatar.position}`] ?? AVATAR_FALLBACK;
}

// ------------------------------------------------------------------- frames

/**
 * The frames, and the rank each is earned at. There is no catalogue endpoint
 * for these — `/profile` reports the ones worn as `av.fr` and nothing
 * enumerates them — so the codes are the client's and must stay stable: they
 * are what `PUT /avatar` sends back.
 */
export interface AvatarFrame {
  id: AvatarFrameId;
  /** Rank index that unlocks it: Master, Oracle, Legend. */
  unlockRank: number;
}

export const AVATAR_FRAMES: readonly AvatarFrame[] = [
  { id: 'GLOW', unlockRank: 4 },
  { id: 'SPARKLES', unlockRank: 5 },
  { id: 'WREATH', unlockRank: 6 },
];

export function isFrameUnlocked(frame: AvatarFrame, rankIndex: number | null): boolean {
  return rankIndex !== null && rankIndex >= frame.unlockRank;
}

/** The winged diadem the Legend frame sets under the portrait (Figma image 6975). */
export const AVATAR_WREATH_ART = REWARDS_ART['avatar-wreath.webp'];

/**
 * Three sparks the Oracle frame scatters around the portrait, with where each
 * sits — Figma 13878:183036, the same spots the `avatar-sparkles` Lottie
 * twinkles them at.
 */
export const AVATAR_SPARKS: readonly { src: string; top: string; left: string; size: number }[] = [
  { src: REWARDS_ART['avatar-sparkle-1.svg'], top: '0%', left: '0%', size: 0.265 },
  { src: REWARDS_ART['avatar-sparkle-2.svg'], top: '50%', left: '84%', size: 0.269 },
  { src: REWARDS_ART['avatar-sparkle-3.svg'], top: '70%', left: '0%', size: 0.26 },
];

/**
 * The Master frame is light rather than artwork, so it is drawn: a thin white
 * rim and a soft white halo round it (Figma: a 5–6 px white drop shadow at
 * half strength over the portrait's lit edge). The halo grows with the box,
 * capped where Figma's 120 px hero has it.
 */
export function avatarGlowRing(size: number): string {
  const blur = Math.min(6, Math.max(2, Math.round(size * 0.1)));
  return `0 0 0 1px rgba(255,255,255,0.7), 0 0 ${blur}px ${blur / 4}px rgba(255,255,255,0.5)`;
}
